import React, { useContext, useEffect, useReducer, useState } from "react";

import {
  Button,
  Card,
  Container,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { useTheme } from '@mui/material/styles';
import ConfirmationModal from "../../components/ConfirmationModal";
import CreateOrEditCompanyAddressModal from "../../components/CreateOrEditCompanyAddressModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
// routes
import api from "../../services/api";
// hooks
import useSettings from '../../hooks/useSettings';
// components
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
// sections
import socket from "../../hooks/useSocket";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";


const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANY_ADDRESSES") {
    const companyAddresses = action.payload;
    const newCompanyAddresses = [];

    companyAddresses.forEach((companyAddress) => {
      const companyAddressIndex = state.findIndex((a) => a.id === companyAddress.id);
      if (companyAddressIndex !== -1) {
        state[companyAddressIndex] = companyAddress;
      } else {
        newCompanyAddresses.push(companyAddress);
      }
    });

    return [...state, ...newCompanyAddresses];
  }

  if (action.type === "UPDATE_COMPANY_ADDRESSES") {
    const companyAddress = action.payload;
    const companyAddressIndex = state.findIndex((a) => a.id === companyAddress.id);

    if (companyAddressIndex !== -1) {
      state[companyAddressIndex] = companyAddress;
      return [...state];
    } else {
      return [companyAddress, ...state];
    }
  }

  if (action.type === "DELETE_COMPANY_ADDRESSES") {
    const companyAddressId = action.payload;

    const companyAddressIndex = state.findIndex((a) => a.id === companyAddressId);
    if (companyAddressIndex !== -1) {
      state.splice(companyAddressIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles()((theme) => {
  return {
    mainPaper: {
      flex: 1,
      padding: theme.spacing(1),
      overflowY: "scroll",
      ...theme.scrollbarStyles,
    },
  }
});

const CompanyAddresses = () => {
  const { classes } = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [companyAddresses, dispatch] = useReducer(reducer, []);
  const [selectedCompanyAddress, setSelectedCompanyAddress] = useState(null);
  const [companyAddressModalOpen, setCompanyAddressModalOpen] = useState(false);
  const [deletingCompanyAddress, setDeletingCompanyAddress] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  // ----------------------------------------------------------------------
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (filterName) => {
    setFilterName(filterName);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - companyAddresses.length) : 0;

  const filteredAddresses = applySortFilter(companyAddresses, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredAddresses.length && Boolean(filterName);
  // ----------------------------------------------------------------------

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompanyAddresses = async () => {
        try {
          const { data } = await api.get("/companyAddresses/", {
            params: { searchParam, pageNumber },
          });
          var FiltroEnderecos = []
          for (let i = 0; i < data.companyAddresses.length; i++) {
            if (data.companyAddresses[i].companyId == companyId) {
              FiltroEnderecos.push(data.companyAddresses[i])
            }
          }
          dispatch({ type: "LOAD_COMPANY_ADDRESSES", payload: FiltroEnderecos });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          ToastError(err);
        }
      };
      fetchCompanyAddresses();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    socket.on("companyAddress", (data) => {
      if (data.action === "update" || data.action === "create") {
        if (!(companyId === data.companyAddress.companyId)) return;
        dispatch({ type: "UPDATE_COMPANY_ADDRESSES", payload: data.companyAddress });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_COMPANY_ADDRESSES",
          payload: +data.companyAddressId,
        });
      }
    });

    return () => {
      socket.off("companyAddress");
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenCompanyAddressModal = () => {
    setSelectedCompanyAddress(null);
    setCompanyAddressModalOpen(true);
  };

  const handleCloseCompanyAddressModal = () => {
    setSelectedCompanyAddress(null);
    setCompanyAddressModalOpen(false);
  };

  const handleEditCompanyAddress = (companyAddress) => {
    setSelectedCompanyAddress(companyAddress);
    setCompanyAddressModalOpen(true);
  };

  const handleDeleteCompanyAddress = async (companyAddressId) => {
    try {
      await api.delete(`/companyAddresses/${companyAddressId}`);
      ToastSuccess("Endereço deletado com sucesso!");
    } catch (err) {
      ToastError(err);
    }
    setDeletingCompanyAddress(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const openMaps = (mapsUrl) => {
    if (mapsUrl) {
      window.open(mapsUrl, '_blank');
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <>
      <div style={{ marginTop: '65px' }}>
        <ConfirmationModal
          title={
            deletingCompanyAddress &&
            `Deletar endereço ${deletingCompanyAddress.name}?`
          }
          open={confirmModalOpen}
          onClose={setConfirmModalOpen}
          onConfirm={() => handleDeleteCompanyAddress(deletingCompanyAddress.id)}
        >
          Tem certeza que deseja deletar este endereço?
        </ConfirmationModal>
        <CreateOrEditCompanyAddressModal
          open={companyAddressModalOpen}
          onClose={handleCloseCompanyAddressModal}
          aria-labelledby="form-dialog-title"
          companyAddressId={selectedCompanyAddress && selectedCompanyAddress.id}
        />
        <Page title="Endereços da Empresa">
        <Container maxWidth={false}>
            <HeaderBreadcrumbs
              heading="Endereços da Empresa"
              action={
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                    onClick={handleOpenCompanyAddressModal}
                  >
                    Adicionar Endereço
                  </Button>
                </>
              }
            />
            <Card>
              <TextField
                style={{ padding: '20px' }}
                placeholder="Pesquisar endereços..."
                type="search"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Nome</TableCell>
                        <TableCell align="center">Endereço</TableCell>
                        <TableCell align="center">Localização</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companyAddresses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                        const { id, name, address, lat, lng, mapsUrl } = row;
                        const isItemSelected = selected.indexOf(name) !== -1;
                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell align="center">{name}</TableCell>
                            <TableCell align="center">{address}</TableCell>
                            <TableCell align="center">
                              {(lat && lng) ? (
                                <span>
                                  {lat}, {lng}
                                  {mapsUrl && (
                                    <IconButton
                                      size="small"
                                      onClick={() => openMaps(mapsUrl)}
                                      style={{ marginLeft: 8 }}
                                    >
                                      <LocationOnIcon />
                                    </IconButton>
                                  )}
                                </span>
                              ) : (
                                "Não informado"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCompanyAddress(row)}
                              >
                                <Edit />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setConfirmModalOpen(true);
                                  setDeletingCompanyAddress(row);
                                }}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                      {loading && <TableRowSkeleton columns={4} />}
                    </TableBody>
                    {isNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={filterName} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
              <TablePagination style={{ marginTop: 15 }}
                labelDisplayedRows={({ from, to, count }) => `${i18n.t("userType.translate.result")} ${count} - ${i18n.t("userType.translate.page")} ${from} ${i18n.t("userType.translate.of")} ${to}`}
                labelRowsPerPage={i18n.t("userType.translate.resultsPerPage")}
                rowsPerPageOptions={[5, 20, 40, 60, 80, 100]}
                component="div"
                count={companyAddresses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, page) => setPage(page)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </Container>
        </Page>
      </div>
    </>
  );
};

export default CompanyAddresses;

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((_address) => 
      _address.name.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
      _address.address.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}